import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { 
  CarritoItem, 
  CarritoState, 
  Cliente, 
  ProductoBusqueda,
  AperturaCaja,
  VentaPausada
} from '@/types';
import type { ModificadorItem } from '@/api/restauranteService';

// Tipo para promociones aplicadas
interface PromocionAplicada {
  promocionId: string;
  codigo: string;
  nombre: string;
  tipoPromocion: string;
  descuentoAplicado: number;
  productosAfectados: string[];
}

interface POSState {
  // Estado del carrito
  carrito: CarritoState & {
    promocionesAplicadas: PromocionAplicada[];
    descuentoPromociones: number;
  };
  // Caja
  aperturaCaja: AperturaCaja | null;
  cajaAbierta: boolean;
  // Ventas pausadas
  ventasPausadas: VentaPausada[];
  // UI del POS
  categoriaSeleccionada: string | null;
  busqueda: string;
  modoCobroActivo: boolean;
  // Historial para deshacer
  historial: (CarritoState & { promocionesAplicadas: PromocionAplicada[]; descuentoPromociones: number })[];
  historialIndex: number;
}

const carritoInicial: CarritoState & { promocionesAplicadas: PromocionAplicada[]; descuentoPromociones: number } = {
  items: [],
  clienteId: undefined,
  cliente: undefined,
  subtotal: 0,
  descuentoGlobal: 0,
  tipoDescuentoGlobal: 'Porcentaje',
  descuentoTotal: 0,
  impuestosTotal: 0,
  total: 0,
  notas: undefined,
  promocionesAplicadas: [],
  descuentoPromociones: 0,
};

const initialState: POSState = {
  carrito: carritoInicial,
  aperturaCaja: null,
  cajaAbierta: false,
  ventasPausadas: [],
  categoriaSeleccionada: null,
  busqueda: '',
  modoCobroActivo: false,
  historial: [],
  historialIndex: -1,
};

// Función para recalcular totales
const recalcularTotales = (state: POSState) => {
  const { items, descuentoGlobal, tipoDescuentoGlobal, descuentoPromociones } = state.carrito;

  // Calcular subtotal (suma de subtotales de items)
  const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
  
  // Calcular impuestos (suma de impuestos de items)
  const impuestosItems = items.reduce((sum, item) => sum + item.impuestos, 0);

  // Calcular descuento de items
  let descuentoItems = items.reduce((sum, item) => {
    if (item.tipoDescuento === 'Porcentaje') {
      return sum + (item.precioUnitario * item.cantidad * item.descuento / 100);
    }
    return sum + item.descuento;
  }, 0);

  // Calcular descuento total (items + promociones + global)
  let descuentoTotal = descuentoItems + descuentoPromociones;

  // Aplicar descuento global
  const subtotalConDescuentos = subtotal - descuentoTotal;
  if (descuentoGlobal > 0) {
    if (tipoDescuentoGlobal === 'Porcentaje') {
      descuentoTotal += subtotalConDescuentos * descuentoGlobal / 100;
    } else {
      descuentoTotal += descuentoGlobal;
    }
  }

  // Total final
  const total = subtotal - descuentoTotal + impuestosItems;

  state.carrito.subtotal = Math.round(subtotal * 100) / 100;
  state.carrito.descuentoTotal = Math.round(descuentoTotal * 100) / 100;
  state.carrito.impuestosTotal = Math.round(impuestosItems * 100) / 100;
  state.carrito.total = Math.round(Math.max(0, total) * 100) / 100;
};

// Función para guardar en historial
const guardarHistorial = (state: POSState) => {
  // Eliminar estados futuros si estamos en medio del historial
  if (state.historialIndex < state.historial.length - 1) {
    state.historial = state.historial.slice(0, state.historialIndex + 1);
  }
  // Agregar estado actual
  state.historial.push(JSON.parse(JSON.stringify(state.carrito)));
  state.historialIndex = state.historial.length - 1;
  // Limitar historial a 50 estados
  if (state.historial.length > 50) {
    state.historial.shift();
    state.historialIndex--;
  }
};

const posSlice = createSlice({
  name: 'pos',
  initialState,
  reducers: {
    // === CARRITO ===
    
    // Agregar producto al carrito
    addItem: (state, action: PayloadAction<ProductoBusqueda & {
      cantidad?: number;
      modificadores?: ModificadorItem[];
      notas?: string;
    }>) => {
      guardarHistorial(state);
      
      const producto = action.payload;
      const cantidad = producto.cantidad || 1;
      const conOpciones = (producto.modificadores?.length ?? 0) > 0 || !!producto.notas;
      
      // Buscar si ya existe el producto (las líneas con modificadores o notas
      // nunca se agrupan: "sin cebolla" y "normal" son líneas distintas)
      const existingIndex = conOpciones ? -1 : state.carrito.items.findIndex(
        (item) => item.productoId === producto.id && !item.productoVarianteId
          && !(item.modificadores?.length) && !item.notas
      );

      if (existingIndex >= 0) {
        // Incrementar cantidad
        const item = state.carrito.items[existingIndex];
        item.cantidad += cantidad;
        item.subtotal = item.precioUnitario * item.cantidad;
        item.impuestos = (item.precioUnitarioConImpuesto - item.precioUnitario) * item.cantidad;
        item.total = item.precioUnitarioConImpuesto * item.cantidad;
      } else {
        // Agregar nuevo item
        const precioSinImpuesto = producto.precio;
        const precioConImpuesto = producto.precioConImpuesto;
        
        const newItem: CarritoItem = {
          id: uuidv4(),
          productoId: producto.id,
          codigo: producto.codigo,
          nombre: producto.nombre,
          imagen: producto.imagen,
          cantidad,
          precioUnitario: precioSinImpuesto,
          precioUnitarioConImpuesto: precioConImpuesto,
          descuento: 0,
          tipoDescuento: 'Porcentaje',
          subtotal: precioSinImpuesto * cantidad,
          impuestos: (precioConImpuesto - precioSinImpuesto) * cantidad,
          total: precioConImpuesto * cantidad,
          stockDisponible: producto.stockActual,
          controlaInventario: producto.controlaInventario,
          modificadores: producto.modificadores?.length ? producto.modificadores : undefined,
          notas: producto.notas || undefined,
        };
        
        state.carrito.items.push(newItem);
      }

      recalcularTotales(state);
    },

    // Actualizar cantidad de un item
    updateItemQuantity: (state, action: PayloadAction<{ id: string; cantidad: number }>) => {
      guardarHistorial(state);
      
      const { id, cantidad } = action.payload;
      const item = state.carrito.items.find((i) => i.id === id);
      
      if (item && cantidad > 0) {
        item.cantidad = cantidad;
        item.subtotal = item.precioUnitario * cantidad;
        item.impuestos = (item.precioUnitarioConImpuesto - item.precioUnitario) * cantidad;
        item.total = item.precioUnitarioConImpuesto * cantidad;
        recalcularTotales(state);
      }
    },

    // Incrementar cantidad
    incrementItem: (state, action: PayloadAction<string>) => {
      const item = state.carrito.items.find((i) => i.id === action.payload);
      if (item) {
        posSlice.caseReducers.updateItemQuantity(state, {
          type: 'pos/updateItemQuantity',
          payload: { id: action.payload, cantidad: item.cantidad + 1 },
        });
      }
    },

    // Decrementar cantidad
    decrementItem: (state, action: PayloadAction<string>) => {
      const item = state.carrito.items.find((i) => i.id === action.payload);
      if (item && item.cantidad > 1) {
        posSlice.caseReducers.updateItemQuantity(state, {
          type: 'pos/updateItemQuantity',
          payload: { id: action.payload, cantidad: item.cantidad - 1 },
        });
      }
    },

    // Eliminar item
    removeItem: (state, action: PayloadAction<string>) => {
      guardarHistorial(state);
      state.carrito.items = state.carrito.items.filter((i) => i.id !== action.payload);
      recalcularTotales(state);
    },

    // Aplicar descuento a un item
    applyItemDiscount: (
      state,
      action: PayloadAction<{
        id: string;
        descuento: number;
        tipoDescuento: 'Porcentaje' | 'Monto';
      }>
    ) => {
      guardarHistorial(state);
      
      const { id, descuento, tipoDescuento } = action.payload;
      const item = state.carrito.items.find((i) => i.id === id);
      
      if (item) {
        item.descuento = descuento;
        item.tipoDescuento = tipoDescuento;
        recalcularTotales(state);
      }
    },

    // Aplicar descuento global
    applyGlobalDiscount: (
      state,
      action: PayloadAction<{
        descuento: number;
        tipoDescuento: 'Porcentaje' | 'Monto';
      }>
    ) => {
      guardarHistorial(state);
      state.carrito.descuentoGlobal = action.payload.descuento;
      state.carrito.tipoDescuentoGlobal = action.payload.tipoDescuento;
      recalcularTotales(state);
    },

    // Establecer cliente
    setCliente: (state, action: PayloadAction<Cliente | undefined>) => {
      state.carrito.clienteId = action.payload?.id;
      state.carrito.cliente = action.payload;
    },

    // Notas de una línea (para cocina)
    setItemNotas: (state, action: PayloadAction<{ id: string; notas?: string }>) => {
      const item = state.carrito.items.find((i) => i.id === action.payload.id);
      if (item) item.notas = action.payload.notas || undefined;
    },

    // Establecer notas
    setNotas: (state, action: PayloadAction<string | undefined>) => {
      state.carrito.notas = action.payload;
    },

    // Limpiar carrito
    clearCarrito: (state) => {
      state.carrito = { ...carritoInicial };
      state.historial = [];
      state.historialIndex = -1;
    },

    // === PROMOCIONES ===
    
    // Aplicar promoción al carrito
    applyPromotion: (state, action: PayloadAction<{ 
      promocionId: string; 
      codigo?: string;
      nombre?: string;
      tipoPromocion?: string;
      descuento?: number;
      productosAfectados?: string[];
    }>) => {
      guardarHistorial(state);
      
      const { promocionId, codigo, nombre, tipoPromocion, descuento, productosAfectados } = action.payload;
      
      // Verificar si ya está aplicada
      const yaAplicada = state.carrito.promocionesAplicadas.some(p => p.promocionId === promocionId);
      if (yaAplicada) return;
      
      // Calcular descuento (simulado - en producción vendrá del backend)
      const descuentoCalculado = descuento || state.carrito.subtotal * 0.10; // Default 10%
      
      const nuevaPromocion: PromocionAplicada = {
        promocionId,
        codigo: codigo || `PROMO${promocionId}`,
        nombre: nombre || `Promoción ${promocionId}`,
        tipoPromocion: tipoPromocion || 'PORCENTAJE',
        descuentoAplicado: descuentoCalculado,
        productosAfectados: productosAfectados || [],
      };
      
      state.carrito.promocionesAplicadas.push(nuevaPromocion);
      state.carrito.descuentoPromociones = state.carrito.promocionesAplicadas.reduce(
        (sum, p) => sum + p.descuentoAplicado, 0
      );
      
      recalcularTotales(state);
    },

    // Remover promoción del carrito
    removePromotion: (state, action: PayloadAction<{ promocionId: string }>) => {
      guardarHistorial(state);
      
      state.carrito.promocionesAplicadas = state.carrito.promocionesAplicadas.filter(
        p => p.promocionId !== action.payload.promocionId
      );
      
      state.carrito.descuentoPromociones = state.carrito.promocionesAplicadas.reduce(
        (sum, p) => sum + p.descuentoAplicado, 0
      );
      
      recalcularTotales(state);
    },

    // Limpiar todas las promociones
    clearPromotions: (state) => {
      guardarHistorial(state);
      state.carrito.promocionesAplicadas = [];
      state.carrito.descuentoPromociones = 0;
      recalcularTotales(state);
    },

    // === DESHACER/REHACER ===
    
    undo: (state) => {
      if (state.historialIndex > 0) {
        state.historialIndex--;
        state.carrito = JSON.parse(JSON.stringify(state.historial[state.historialIndex]));
      }
    },

    redo: (state) => {
      if (state.historialIndex < state.historial.length - 1) {
        state.historialIndex++;
        state.carrito = JSON.parse(JSON.stringify(state.historial[state.historialIndex]));
      }
    },

    // === CAJA ===
    
    setAperturaCaja: (state, action: PayloadAction<AperturaCaja | null>) => {
      state.aperturaCaja = action.payload;
      state.cajaAbierta = action.payload !== null && action.payload.estado === 'Abierta';
    },

    // === VENTAS PAUSADAS ===
    
    pausarVenta: (state, action: PayloadAction<{ descripcion?: string }>) => {
      if (state.carrito.items.length > 0) {
        const ventaPausada: VentaPausada = {
          id: uuidv4(),
          carrito: JSON.parse(JSON.stringify(state.carrito)),
          fechaPausa: new Date().toISOString(),
          usuarioId: '', // Se establece desde el componente
          descripcion: action.payload.descripcion,
        };
        state.ventasPausadas.push(ventaPausada);
        state.carrito = { ...carritoInicial };
      }
    },

    reanudarVenta: (state, action: PayloadAction<string>) => {
      const index = state.ventasPausadas.findIndex((v) => v.id === action.payload);
      if (index >= 0) {
        const carritoGuardado = state.ventasPausadas[index].carrito;
        state.carrito = {
          ...carritoGuardado,
          promocionesAplicadas: (carritoGuardado as any).promocionesAplicadas || [],
          descuentoPromociones: (carritoGuardado as any).descuentoPromociones || 0,
        };
        state.ventasPausadas.splice(index, 1);
      }
    },

    eliminarVentaPausada: (state, action: PayloadAction<string>) => {
      state.ventasPausadas = state.ventasPausadas.filter((v) => v.id !== action.payload);
    },

    // === UI ===
    
    setCategoriaSeleccionada: (state, action: PayloadAction<string | null>) => {
      state.categoriaSeleccionada = action.payload;
    },

    setBusqueda: (state, action: PayloadAction<string>) => {
      state.busqueda = action.payload;
    },

    setModoCobroActivo: (state, action: PayloadAction<boolean>) => {
      state.modoCobroActivo = action.payload;
    },

    // Restaurar estado completo (para offline)
    restoreState: (state, action: PayloadAction<Partial<POSState>>) => {
      return { ...state, ...action.payload };
    },
  },
});

export const {
  addItem,
  updateItemQuantity,
  incrementItem,
  decrementItem,
  removeItem,
  applyItemDiscount,
  applyGlobalDiscount,
  setCliente,
  setNotas,
  setItemNotas,
  clearCarrito,
  applyPromotion,
  removePromotion,
  clearPromotions,
  undo,
  redo,
  setAperturaCaja,
  pausarVenta,
  reanudarVenta,
  eliminarVentaPausada,
  setCategoriaSeleccionada,
  setBusqueda,
  setModoCobroActivo,
  restoreState,
} = posSlice.actions;

export default posSlice.reducer;